import { Box, DialogActions, Grid, styled } from '@mui/material';
import LanguageSelectorItem from 'Components/Forms/LanguageSelectorItem';
import SlugField from 'Components/Forms/SlugField';
import useConfig from 'hooks/useConfig';
import { createProductSchema } from 'FeatureModules/ProductPage/schemas/product.schemas';
import { FieldWrapper, ThemeButton } from 'DesignSystem';
import { FastField as Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import isEqual from 'lodash/isEqual';
import React, { FC, useMemo } from 'react';
import { ILanguageConfig } from 'FeatureModules/ProductPage/types/product.types';
import { ILanguageConfigOption } from 'FeatureModules/ProductPage/types/product.types';
import { IProduct } from 'FeatureModules/ProductPage/types/product.types';
import CreateFormGeneralSection from '../Create/CreateFormGeneralSection';

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(4, 0, 0, 0),
}));

const StyledTextField = styled(TextField)(() => ({
  '&.MuiTextField-root': {
    '& .MuiInputBase-input': {
      padding: '10px 12px',
    },
  },
}));

export interface IProductFormValues {
  title: string;
  slug: string;
  articleNumber: string;
  languageConfig: ILanguageConfig;
}

export type TProductFormType =
  | {
      action: 'CREATE';
      product?: undefined;
    }
  | {
      action: 'DUPLICATE' | 'EDIT';
      product: Pick<IProduct, 'id' | 'title' | 'slug' | 'articleNumber' | 'languageConfig'>;
    };

interface IProductFormProps {
  submitButtonText?: string;
  onSubmit: (values: IProductFormValues) => void;
}

const ProductForm: FC<IProductFormProps & TProductFormType> = ({
  action,
  product,
  submitButtonText,
  onSubmit,
}) => {
  const config = useConfig();
  const productLanguages: ILanguageConfigOption[] = config.productLanguages;

  const filteredProductLanguages = useMemo(() => {
    // You can only change the default and not add a new one here
    if (action === 'EDIT') {
      return productLanguages.filter(({ key }) => product.languageConfig.enabled.includes(key));
    }
    return productLanguages;
  }, [productLanguages, product, action]);

  /**
   * The values are added from the default schema, but types can not be inferred from the schema.
   */
  // @ts-ignore
  const initialValues: IProductFormValues = useMemo(() => {
    switch (action) {
      case 'CREATE':
        return createProductSchema.default();
      case 'DUPLICATE':
        return {
          ...createProductSchema.default(),
          title: `${product.title}-Copy`,
          slug: `${product.slug}-copy`,
        };
      case 'EDIT':
        return {
          ...createProductSchema.default(),
          title: product.title,
          slug: product.slug,
          articleNumber: product.articleNumber,
          languageConfig: product.languageConfig,
        };
      default:
        return createProductSchema.default();
    }
  }, [action, product]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createProductSchema}
      onSubmit={values => onSubmit(values)}
      initialStatus={{
        slugValidationDone: action === 'EDIT',
        slugAvailable: true,
      }}
      validateOnChange
    >
      {({ status, isSubmitting, values, isValid }) => (
        <Form data-testid="create-product-form">
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <CreateFormGeneralSection />
            </Grid>
            {action !== 'DUPLICATE' && (
              <Grid item xs={12}>
                <FieldWrapper
                  label="Default language"
                  subtitle="Set the default language for your product. You can add more content languages later."
                >
                  <Box maxWidth={400}>
                    <Field
                      variant="outlined"
                      fullWidth
                      component={StyledTextField}
                      name="languageConfig.default"
                      type="text"
                      select
                      data-testid="product-default-language-selector"
                      SelectProps={{
                        renderValue: (value: string) => {
                          const { flag, title } = config.getLanguageConfig(value);

                          return (
                            <LanguageSelectorItem
                              value={value}
                              flag={flag}
                              title={title}
                              noPadding
                              hoverBackground={undefined}
                            />
                          );
                        },
                      }}
                    >
                      {filteredProductLanguages.map(
                        ({ title, key, flag }: { title: string; key: string; flag: string }) => (
                          <LanguageSelectorItem
                            key={`product-lang-${key}`}
                            value={key}
                            flag={flag}
                            title={title}
                            hoverBackground={undefined}
                            noPadding={undefined}
                          />
                        )
                      )}
                    </Field>
                  </Box>
                </FieldWrapper>
              </Grid>
            )}
            {action !== 'EDIT' && (
              <Grid item xs={12}>
                <FieldWrapper
                  label="Product URL"
                  subtitle="This is the path under which your product page will be found on this platform. You edit using <b>letters</b>, <b>numbers</b> and <b>hyphens (-)</b>."
                >
                  <SlugField
                    allowValue={product?.slug}
                    disableAutocomplete={action !== 'CREATE'}
                    name="slug"
                    dependency="title"
                    variant="outlined"
                    fullWidth
                    data-testid="product-slug-input"
                  />
                </FieldWrapper>
              </Grid>
            )}
          </Grid>
          <StyledDialogActions>
            <ThemeButton
              loading={isSubmitting}
              color="PRIMARY"
              size="large"
              className="button-wide"
              type="submit"
              disabled={
                (action !== 'DUPLICATE' && isEqual(initialValues, values)) ||
                (action === 'EDIT'
                  ? !isValid
                  : !status.slugValidationDone || !status.slugAvailable || !isValid)
              }
              data-testid="create-product-button"
            >
              {submitButtonText}
            </ThemeButton>
          </StyledDialogActions>
        </Form>
      )}
    </Formik>
  );
};

export default ProductForm;
